import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, Link } from 'react-router-dom';

import { _getFile } from '@/api/files/queries';
import { useCreateGammaRoleRequest } from '@/api/gamma-role-requests/mutations';
import { _getGammaRoles } from '@/api/gamma-roles/queries';
import { _updateGammaTeamInvitation, _updateGammaTeamMember } from '@/api/gamma-team/mutations';
import {
  GAMMA_TEAM_QUERY_KEYS,
  type GammaTeamMemberRole,
  GammaTeamRole,
  gammaTeamRole,
  selectableGammaTeamRoles,
} from '@/api/gamma-team/types';
import { PERMISSION_METADATA } from '@/api/permissions';
import { _getProfileById } from '@/api/profile/queries';
import type { TeamAssociateStatusType } from '@/api/types';
import LinkedinIcon from '@/assets/svg/socials/linkedin-white.svg';
import XIcon from '@/assets/svg/socials/x-white.svg';
import ExternalLinkIcon from '@/assets/svg/ui-elements/external-link.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { PagePaths } from '@/routes/page-paths';
import {
  Avatar,
  Drawer,
  IconButton,
  Input,
  Select,
  theme,
  Tooltip,
  Typography,
  useToast,
} from '@/shared/design-system';
import { Permission, useCan } from '@/shared/modules/permissions';
import { normalizeExternalUrl } from '@/utils/normalize-external-url';

import {
  FieldsDivider,
  RegisteredUserCard,
  RegisteredUserHeadshot,
  RegisteredUserIdentity,
  RegisteredUserLinks,
  RegisteredUserSection,
  RegisteredUserText,
  RoleFieldTrigger,
} from './gamma-team-member-edit.styled';
import { buildRoleRequests, roleRequestKey } from './gamma-team-member-edit.utils';
import { TeamMemberFields } from './team-member-fields.styled';

export const SUPERADMIN_ROLE_TOOLTIP = 'The Superadmin role cannot be revoked.';
export const ASSIGN_ROLES_DENIED_REASON = `You do not have permission to change roles (${PERMISSION_METADATA[Permission.GammaAssignRoles].label}).`;
export const VIEW_ROLES_DENIED_REASON = `You do not have permission to see Gamma roles (${PERMISSION_METADATA[Permission.GammaViewRoles].label}).`;
export const MANAGE_TEAM_DENIED_REASON = `You do not have permission to change this invitation (${PERMISSION_METADATA[Permission.GammaTeamManage].label}).`;
export const CUSTOM_ROLES_AFTER_SIGN_UP_REASON = 'Available after they sign up';
export const CUSTOM_ROLES_BLOCKED_REASON = 'Not available while this person is blocked';
export const ROLE_REQUEST_SENT_REASON =
  'The role change is already sent. Close the form to choose another role.';
export const ROLES_NOT_FOUND_MESSAGE = 'Could not find these roles. Try again in a moment.';

interface GammaTeamMemberEditForm {
  role: GammaTeamRole | '';
  jobTitle: string;
  customRoleIds: string[];
}

const EMPTY_FORM: GammaTeamMemberEditForm = { role: '', jobTitle: '', customRoleIds: [] };

const customRoleIdsOf = (roles: readonly GammaTeamMemberRole[] | undefined) =>
  (roles ?? []).flatMap(({ id, systemKey }) => (systemKey === null ? [id] : []));

const sameIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

export interface GammaTeamMemberEditData {
  email: string;
  role: GammaTeamRole;
  roles: GammaTeamMemberRole[];
  status: TeamAssociateStatusType;
  jobTitle: string | null;
  userId: string | null;
  invitationId: string | null;
  name: string | null;
  headshotFileId: string | null;
  isSuperAdmin: boolean;
}

export const GammaTeamMemberEdit = () => {
  const { open, handleClose, props } = useGlobalModal('GAMMA_TEAM_MEMBER_EDIT_MODAL');
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<GammaTeamMemberEditForm>(EMPTY_FORM);
  const [isSaving, setSaving] = useState(false);
  const [sentRequestKeys, setSentRequestKeys] = useState<string[]>([]);
  const { add: addToast } = useToast();
  const isSuperAdmin = props?.isSuperAdmin ?? false;
  const isRegisteredUser = props?.userId !== null && props?.userId !== undefined;
  const isInvitation = !!props?.invitationId;
  const canHoldCustomRoles =
    isRegisteredUser && (!isInvitation || props?.status === 'WaitingForApproval');

  const canManageTeam = useCan(Permission.GammaTeamManage);
  const canAssignRoles = useCan(Permission.GammaAssignRoles);
  const canViewRoles = useCan(Permission.GammaViewRoles);

  const { mutateAsync: updateMember } = useMutation({ mutationFn: _updateGammaTeamMember });
  const { mutateAsync: updateInvitation } = useMutation({ mutationFn: _updateGammaTeamInvitation });
  const { mutateAsync: requestRoleChange } = useCreateGammaRoleRequest();
  const roles = useQuery(_getGammaRoles(open && canViewRoles && canHoldCustomRoles));

  const { data: profileData } = useQuery({
    ..._getProfileById(props?.userId ?? undefined),
    enabled: open && isRegisteredUser,
  });

  const { data: headshotFile } = useQuery({
    ..._getFile(props?.headshotFileId as string),
    enabled: open && !!props?.headshotFileId,
  });

  const currentCustomIds = useMemo(() => customRoleIdsOf(props?.roles), [props?.roles]);

  const customRoleOptions = useMemo(() => {
    const namesById = new Map(
      [...(props?.roles ?? []), ...(roles.data?.roles ?? [])]
        .filter(({ systemKey }) => systemKey === null)
        .map(({ id, name }) => [id, name] as const),
    );

    return [...namesById]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [props?.roles, roles.data]);

  const isRoleChanged = props?.role !== formData.role;
  const isJobTitleChanged = (props?.jobTitle ?? '') !== formData.jobTitle;
  const isCustomRolesChanged = !sameIds(currentCustomIds, formData.customRoleIds);
  const isFormChanged = isRoleChanged || isJobTitleChanged || isCustomRolesChanged;

  const roleRequestDeniedReason = !canAssignRoles
    ? ASSIGN_ROLES_DENIED_REASON
    : !canViewRoles
      ? VIEW_ROLES_DENIED_REASON
      : '';
  const roleRequestReason =
    roleRequestDeniedReason || (sentRequestKeys.length > 0 ? ROLE_REQUEST_SENT_REASON : '');
  const builtInRoleReason = isSuperAdmin
    ? SUPERADMIN_ROLE_TOOLTIP
    : isInvitation
      ? canManageTeam
        ? ''
        : MANAGE_TEAM_DENIED_REASON
      : roleRequestReason;
  const customRolesReason = !canHoldCustomRoles
    ? isRegisteredUser
      ? CUSTOM_ROLES_BLOCKED_REASON
      : CUSTOM_ROLES_AFTER_SIGN_UP_REASON
    : roleRequestReason;
  const isBuiltInRoleDisabled =
    !!builtInRoleReason || isSaving || (!isInvitation && !roles.isSuccess);

  useEffect(() => {
    if (props) {
      setFormData({
        role: props.role,
        jobTitle: props.jobTitle ?? '',
        customRoleIds: customRoleIdsOf(props.roles),
      });
      setSentRequestKeys([]);
    }
  }, [props]);

  const handleSave = async () => {
    if (!props) return;

    const roleRequests = buildRoleRequests({
      currentSystemKey: props.role,
      nextSystemKey: isInvitation ? props.role : formData.role,
      currentCustomIds,
      nextCustomIds: canHoldCustomRoles ? formData.customRoleIds : currentCustomIds,
      roles: roles.data?.roles ?? [],
    });
    if (!roleRequests) {
      addToast({ title: ROLES_NOT_FOUND_MESSAGE, data: { status: 'error' } });
      return;
    }

    const unsentRequests = roleRequests.filter(
      (request) => !sentRequestKeys.includes(roleRequestKey(request)),
    );

    setSaving(true);
    try {
      if (props.invitationId && (isRoleChanged || isJobTitleChanged)) {
        await updateInvitation({
          id: props.invitationId,
          ...(isRoleChanged && { role: formData.role as GammaTeamRole }),
          jobTitle: formData.jobTitle || null,
        });
      }

      if (props.userId) {
        for (const request of unsentRequests) {
          await requestRoleChange({
            type: request.type,
            payload: { roleId: request.roleId, userId: props.userId },
          });
          setSentRequestKeys((sent) => [...sent, roleRequestKey(request)]);
        }
      }

      if (!props.invitationId && props.userId && isJobTitleChanged) {
        await updateMember({ userId: props.userId, jobTitle: formData.jobTitle || null });
      }
    } finally {
      setSaving(false);
    }

    handleCloseClick();
    queryClient.invalidateQueries({ queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard] });
  };

  const handleCloseClick = () => {
    setFormData(EMPTY_FORM);
    setSentRequestKeys([]);
    handleClose();
  };

  const roleOptions = isSuperAdmin
    ? [{ label: 'Superadmin', value: gammaTeamRole.Superadmin }, ...selectableGammaTeamRoles]
    : selectableGammaTeamRoles;

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && handleCloseClick()}
      title="Edit Team Member"
      showClose
      secondaryAction={{ children: 'Cancel', onClick: handleCloseClick }}
      primaryAction={{
        children: 'Save',
        onClick: () => void handleSave().catch(() => undefined),
        disabled: !isFormChanged || isSaving,
      }}
    >
      <TeamMemberFields>
        <Input size="lg" label="Email" required value={props?.email || ''} disabled />

        <Tooltip title={builtInRoleReason} side="top" disabled={!builtInRoleReason}>
          <RoleFieldTrigger denied={!!builtInRoleReason}>
            <Select
              size="lg"
              label="Role"
              required
              placeholder="Select role"
              options={roleOptions}
              value={formData.role || null}
              disabled={isBuiltInRoleDisabled}
              onChange={(role) => setFormData((prev) => ({ ...prev, role: role as GammaTeamRole }))}
            />
          </RoleFieldTrigger>
        </Tooltip>

        <Tooltip title={customRolesReason} side="top" disabled={!customRolesReason}>
          <RoleFieldTrigger denied={!!customRolesReason}>
            <Select
              multiple
              size="lg"
              label="Custom roles"
              placeholder="Select custom roles"
              emptyMessage="There are no custom roles yet"
              options={customRoleOptions}
              value={formData.customRoleIds}
              disabled={!!customRolesReason || isSaving}
              onChange={(customRoleIds) => setFormData((prev) => ({ ...prev, customRoleIds }))}
            />
          </RoleFieldTrigger>
        </Tooltip>

        <Input
          size="lg"
          label="Gamma Job Title"
          placeholder="Enter gamma job title"
          value={formData.jobTitle}
          maxLength={100}
          onChange={(event) => setFormData((prev) => ({ ...prev, jobTitle: event.target.value }))}
        />

        {isRegisteredUser && (
          <>
            <FieldsDivider />

            <RegisteredUserSection>
              <Typography variant="body3">
                This user is already registered in Gamma Prime
              </Typography>

              <RegisteredUserCard>
                <RegisteredUserIdentity>
                  {headshotFile?.url ? (
                    <RegisteredUserHeadshot src={headshotFile.url} alt="" />
                  ) : (
                    <Avatar size="lg" initials={props?.name || '?'} />
                  )}
                  <RegisteredUserText>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {props?.name}
                    </Typography>
                    <Typography variant="body4" color={theme.colors.text.secondary} noWrap>
                      {props?.email}
                    </Typography>
                  </RegisteredUserText>
                </RegisteredUserIdentity>

                <RegisteredUserLinks>
                  <Tooltip title="Open profile" side="top">
                    <IconButton
                      size="sm"
                      color="tertiary"
                      variant="subtle"
                      aria-label="Open profile"
                      nativeButton={false}
                      // Navigation, so it is a link: Base UI would otherwise announce a button.
                      role="link"
                      render={
                        <Link
                          to={generatePath(PagePaths.userProfile, { id: props?.userId ?? '' })}
                        />
                      }
                    >
                      <ExternalLinkIcon />
                    </IconButton>
                  </Tooltip>

                  {profileData?.xProfile && (
                    <IconButton
                      size="sm"
                      color="tertiary"
                      variant="subtle"
                      nativeButton={false}
                      role="link"
                      render={
                        <a
                          href={normalizeExternalUrl(profileData.xProfile)}
                          aria-label="X profile"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                    >
                      <XIcon width={16} height={16} />
                    </IconButton>
                  )}

                  {profileData?.linkedinProfile && (
                    <IconButton
                      size="sm"
                      color="tertiary"
                      variant="subtle"
                      nativeButton={false}
                      role="link"
                      render={
                        <a
                          href={normalizeExternalUrl(profileData.linkedinProfile)}
                          aria-label="LinkedIn profile"
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                    >
                      <LinkedinIcon width={16} height={16} />
                    </IconButton>
                  )}
                </RegisteredUserLinks>
              </RegisteredUserCard>
            </RegisteredUserSection>
          </>
        )}
      </TeamMemberFields>
    </Drawer>
  );
};
